import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Edit3, Check, X, RotateCcw, Loader2, Search } from 'lucide-react';
import { COPY_REGISTRY } from '@/config/copyRegistry';
import { useCopyContext } from '@/context/CopyContext';
import { dashboardApi } from '@/services/dashboardApi';
import { useConfirm } from '@/components/dashboard/admin/ConfirmDialog';

/**
 * Admin-facing editor for dashboard copy strings.
 *
 * Renders every entry declared in COPY_REGISTRY, grouped by category. For each
 * entry the admin sees the default value (from code), the current effective
 * value (override if any, otherwise default), and can edit or reset.
 *
 * Why iterate the registry rather than the DB?
 *   The registry is the source of truth for which keys *exist*. The DB only
 *   stores edits. Iterating the registry guarantees the admin sees every key
 *   even if no override has been set yet — they can edit anything in the app.
 */
export default function CopyEditor() {
  const [dialog, ask] = useConfirm();
  const { overrides, updateLocal, removeLocal } = useCopyContext();
  const [editingKey, setEditingKey] = useState(null);
  const [draftEs, setDraftEs] = useState('');
  const [draftEn, setDraftEn] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  // Group registry entries by category for clearer admin UX.
  const grouped = useMemo(() => {
    const filter = search.trim().toLowerCase();
    const filtered = filter
      ? COPY_REGISTRY.filter(e =>
          e.key.toLowerCase().includes(filter) ||
          e.default_es.toLowerCase().includes(filter) ||
          (e.description || '').toLowerCase().includes(filter))
      : COPY_REGISTRY;
    return filtered.reduce((acc, entry) => {
      const cat = entry.category || 'General';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(entry);
      return acc;
    }, {});
  }, [search]);

  const startEdit = (entry) => {
    const override = overrides[entry.key];
    setEditingKey(entry.key);
    setDraftEs(override?.value_es ?? entry.default_es);
    setDraftEn(override?.value_en ?? entry.default_en ?? '');
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraftEs('');
    setDraftEn('');
  };

  const save = async (entry) => {
    if (!draftEs.trim()) {
      toast.error('El texto en español no puede estar vacío');
      return;
    }
    setSaving(true);
    try {
      const result = await dashboardApi.upsertCopy(entry.key, draftEs, draftEn || null);
      updateLocal(entry.key, { value_es: result.value_es, value_en: result.value_en });
      toast.success('Texto actualizado');
      cancelEdit();
    } catch (e) {
      toast.error(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const reset = async (entry) => {
    if (!overrides[entry.key]) return;
    const ok = await ask({
      title: 'Restaurar el texto original',
      description: `«${entry.description}» vuelve al texto por defecto y se pierde la versión editada.`,
      confirmLabel: 'Restaurar', tone: 'gold',
    });
    if (!ok) return;
    setSaving(true);
    try {
      await dashboardApi.deleteCopy(entry.key);
      removeLocal(entry.key);
      toast.success('Restaurado al valor por defecto');
    } catch (e) {
      toast.error(e.message || 'Error al restaurar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {dialog}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-white text-sm font-semibold">Editor de textos del panel</h3>
          <p className="text-gray-400 text-xs mt-1">
            Cambios visibles inmediatamente para todos los usuarios. Los textos sin editar muestran el valor por defecto.
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar texto…"
            className="pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#c9a84c]/40 w-full sm:w-64"
          />
        </div>
      </div>

      {/* Categories */}
      {Object.keys(grouped).length === 0 ? (
        <p className="text-gray-400 text-sm py-8 text-center">No hay coincidencias para «{search}»</p>
      ) : (
        Object.entries(grouped).map(([category, entries]) => (
          <section key={category}>
            <h4 className="text-[11px] uppercase tracking-wider text-[#c9a84c]/70 font-semibold mb-3">
              {category}
            </h4>
            <div className="space-y-2">
              {entries.map(entry => {
                const isOverridden = !!overrides[entry.key];
                const currentEs = isOverridden ? overrides[entry.key].value_es : entry.default_es;
                const currentEn = isOverridden ? overrides[entry.key].value_en : entry.default_en;
                const isEditing = editingKey === entry.key;

                return (
                  <div
                    key={entry.key}
                    className={`rounded-xl border p-3 transition-colors ${
                      isEditing
                        ? 'bg-[#c9a84c]/[0.04] border-[#c9a84c]/30'
                        : isOverridden
                          ? 'bg-emerald-500/[0.03] border-emerald-500/20'
                          : 'bg-white/[0.02] border-white/[0.06]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-300">{entry.description}</p>
                        <p className="text-[11px] text-gray-400 font-mono mt-0.5">{entry.key}</p>
                      </div>
                      {isOverridden && !isEditing && (
                        <span className="text-[11px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                          editado
                        </span>
                      )}
                    </div>

                    {isEditing ? (
                      <div className="space-y-2">
                        <div>
                          <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block">Texto (ES)</label>
                          <textarea
                            value={draftEs}
                            onChange={(e) => setDraftEs(e.target.value)}
                            rows={Math.max(1, Math.min(4, Math.ceil(draftEs.length / 60)))}
                            className="w-full px-3 py-2 rounded-lg bg-[#060d1b] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-[#c9a84c]/40 resize-none"
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block">
                            Texto (EN) — opcional
                          </label>
                          <textarea
                            value={draftEn}
                            onChange={(e) => setDraftEn(e.target.value)}
                            rows={Math.max(1, Math.min(4, Math.ceil((draftEn || '').length / 60) || 1))}
                            placeholder="Dejar vacío para usar el valor por defecto"
                            className="w-full px-3 py-2 rounded-lg bg-[#060d1b] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-[#c9a84c]/40 resize-none"
                          />
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={() => save(entry)}
                            disabled={saving}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#c9a84c] hover:bg-[#d4b85c] disabled:opacity-50 text-[#0a1225] text-xs font-semibold transition-colors"
                          >
                            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                            Guardar
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={saving}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-gray-400 hover:bg-white/[0.04] text-xs"
                          >
                            <X size={12} /> Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-white text-sm leading-relaxed mb-1.5 whitespace-pre-wrap break-words">
                          {currentEs}
                        </p>
                        {currentEn && (
                          <p className="text-gray-400 text-xs italic mb-1.5 whitespace-pre-wrap break-words">
                            EN: {currentEn}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => startEdit(entry)}
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:bg-white/[0.04] text-xs transition-colors"
                          >
                            <Edit3 size={11} /> Editar
                          </button>
                          {isOverridden && (
                            <button
                              onClick={() => reset(entry)}
                              disabled={saving}
                              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-amber-500/20 text-amber-400/70 hover:text-amber-300 hover:bg-amber-500/10 text-xs transition-colors disabled:opacity-50"
                            >
                              <RotateCcw size={11} /> Restaurar por defecto
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
